import React, { useState, useEffect } from 'react'
import { X, RefreshCw, CheckCircle, Loader } from 'lucide-react'
import { whatsappMailboxService } from '@/services/whatsapp-mailbox.service'

interface WhatsAppQRCodeModalProps {
  mailboxId: number
  mailboxName: string
  onClose: () => void
  onConnected: () => void
}

export const WhatsAppQRCodeModal: React.FC<WhatsAppQRCodeModalProps> = ({
  mailboxId,
  mailboxName,
  onClose,
  onConnected,
}) => {
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [, setExpiresIn] = useState<number>(60)
  const [timeRemaining, setTimeRemaining] = useState<number>(60)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [connectionStatus, setConnectionStatus] = useState<'waiting' | 'connected' | 'failed'>('waiting')

  // Generate QR code on mount
  useEffect(() => {
    generateQRCode()
  }, [])

  // Countdown timer
  useEffect(() => {
    if (connectionStatus !== 'waiting' || timeRemaining <= 0) return

    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          // QR code expired
          setError('QR code expired. Please generate a new one.')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [timeRemaining, connectionStatus])

  const generateQRCode = async () => {
    try {
      setLoading(true)
      setError(null)
      setConnectionStatus('waiting')

      const response = await whatsappMailboxService.generateQRCode(mailboxId)

      setQrCode(response.qrCode)
      setExpiresIn(response.expiresIn)
      setTimeRemaining(response.expiresIn)
      setLoading(false)

      // Start polling for connection status
      startConnectionPolling()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate QR code')
      setLoading(false)
    }
  }

  const startConnectionPolling = () => {
    const pollInterval = setInterval(async () => {
      try {
        const mailbox = await whatsappMailboxService.getWhatsAppMailbox(mailboxId)

        if (mailbox.isConnected) {
          setConnectionStatus('connected')
          clearInterval(pollInterval)

          // Notify parent component and close modal after 2 seconds
          setTimeout(() => {
            onConnected()
            onClose()
          }, 2000)
        }
      } catch (err) {
        // Ignore polling errors
      }
    }, 2000) // Poll every 2 seconds

    // Clear interval on unmount or when connection fails
    return () => clearInterval(pollInterval)
  }

  const handleRegenerate = () => {
    generateQRCode()
  }

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
          disabled={loading}
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Connect WhatsApp</h2>
          <p className="mt-1 text-sm text-gray-600">
            Scan this QR code with WhatsApp on your phone
          </p>
          <p className="text-xs text-gray-500 mt-1">Mailbox: {mailboxName}</p>
        </div>

        {/* QR Code Display */}
        <div className="mb-6 flex flex-col items-center">
          {loading ? (
            <div className="flex h-64 w-64 items-center justify-center rounded-lg bg-gray-100">
              <Loader className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : error ? (
            <div className="flex h-64 w-64 flex-col items-center justify-center rounded-lg bg-red-50 p-4">
              <p className="text-center text-sm text-red-600">{error}</p>
              <button
                onClick={handleRegenerate}
                className="mt-4 flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
              >
                <RefreshCw size={16} />
                Generate New QR Code
              </button>
            </div>
          ) : connectionStatus === 'connected' ? (
            <div className="flex h-64 w-64 flex-col items-center justify-center rounded-lg bg-green-50">
              <CheckCircle className="h-16 w-16 text-green-600" />
              <p className="mt-4 text-lg font-semibold text-green-600">Connected!</p>
              <p className="text-sm text-gray-600">Redirecting...</p>
            </div>
          ) : qrCode ? (
            <>
              <div className="rounded-lg border-4 border-gray-200 bg-white p-4">
                <img
                  src={qrCode}
                  alt="WhatsApp QR Code"
                  className="h-64 w-64"
                />
              </div>

              {/* Timer */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  QR code expires in:{' '}
                  <span className={`font-semibold ${timeRemaining <= 10 ? 'text-red-600' : 'text-primary-600'}`}>
                    {formatTime(timeRemaining)}
                  </span>
                </p>
                {timeRemaining <= 10 && (
                  <button
                    onClick={handleRegenerate}
                    className="mt-2 flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <RefreshCw size={14} />
                    Regenerate QR Code
                  </button>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Instructions */}
        {!loading && !error && connectionStatus === 'waiting' && (
          <div className="rounded-lg bg-blue-50 p-4">
            <h3 className="mb-2 text-sm font-semibold text-blue-900">How to connect:</h3>
            <ol className="space-y-1 text-sm text-blue-800">
              <li>1. Open WhatsApp on your phone</li>
              <li>2. Tap Menu or Settings</li>
              <li>3. Tap Linked Devices</li>
              <li>4. Tap Link a Device</li>
              <li>5. Point your phone at this screen to scan the QR code</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  )
}
